/**
 * Translation Index
 * Exports all translation files and provides language lookup functionality
 */

import en from './en.json';
import ar from './ar.json';
import de from './de.json';
import fr from './fr.json';
import es from './es.json';
import pt from './pt.json';
import ur from './ur.json';
import nl from './nl.json';
import it from './it.json';
import ja from './ja.json';
import zh from './zh.json';
import hi from './hi.json';
import ru from './ru.json';
import tr from './tr.json';
import ko from './ko.json';
import th from './th.json';
import vi from './vi.json';
import idLang from './id.json';
import ms from './ms.json';
import pl from './pl.json';
import sv from './sv.json';
import da from './da.json';
import nbLang from './nb.json';
import fi from './fi.json';
import cs from './cs.json';
import hu from './hu.json';
import ro from './ro.json';
import el from './el.json';
import bg from './bg.json';
import uk from './uk.json';
import hr from './hr.json';
import sr from './sr.json';
import sl from './sl.json';
import lv from './lv.json';
import et from './et.json';
import lt from './lt.json';
import ca from './ca.json';
import ta from './ta.json';
import sk from './sk.json';
import he from './he.json';

// Map of all available translations
// Key is the language code (uppercase), value is the translation object
export const translations = {
  EN: en,
  AR: ar,
  DE: de,
  FR: fr,
  ES: es,
  PT: pt,
  UR: ur,
  NL: nl,
  IT: it,
  JA: ja,
  ZH: zh,
  HI: hi,
  RU: ru,
  TR: tr,
  KO: ko,
  TH: th,
  VI: vi,
  ID: idLang,
  MS: ms,
  PL: pl,
  SV: sv,
  DA: da,
  NB: nbLang,
  FI: fi,
  CS: cs,
  HU: hu,
  RO: ro,
  EL: el,
  BG: bg,
  UK: uk,
  HR: hr,
  SR: sr,
  SL: sl,
  LV: lv,
  ET: et,
  LT: lt,
  CA: ca,
  TA: ta,
  SK: sk,
  HE: he
};

// Default language
export const defaultLanguage = 'EN';

/**
 * Get translations for a specific language code
 * Falls back to English if language not found
 * @param {string} languageCode - Language code (e.g., 'EN', 'AR', 'DE')
 * @returns {Object} Translation object for the specified language
 */
export const getTranslations = (languageCode) => {
  const code = languageCode?.toUpperCase() || defaultLanguage;
  return translations[code] || translations[defaultLanguage];
};

/**
 * Get a specific translation key with nested path support
 * @param {string} languageCode - Language code
 * @param {string} path - Dot-separated path to the translation key (e.g., 'header.signIn')
 * @param {Object} params - Optional parameters for string interpolation
 * @returns {string} Translated string or the path if not found
 */
export const getTranslation = (languageCode, path, params = {}) => {
  const keys = path.split('.');

  // Look up the dot-path inside one translation object; returns the string or null.
  const lookup = (trans) => {
    let result = trans;
    for (const key of keys) {
      if (result && typeof result === 'object' && key in result) {
        result = result[key];
      } else {
        return null;
      }
    }
    return typeof result === 'string' ? result : null;
  };

  // Try the requested language, then fall back to English (default), then the raw path.
  let result = lookup(getTranslations(languageCode));
  if (result === null && String(languageCode).toUpperCase() !== defaultLanguage) {
    result = lookup(translations[defaultLanguage]);
  }
  if (result === null) {
    // Return the path if key not found in any language (dev signal)
    return path;
  }

  // Replace parameters in the string (e.g., {n}, {price}, {percent})
  let finalResult = result;
  for (const [paramKey, paramValue] of Object.entries(params)) {
    finalResult = finalResult.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), paramValue);
  }

  return finalResult;
};

/**
 * Check if a language is supported
 * @param {string} languageCode - Language code to check
 * @returns {boolean} True if the language is supported
 */
export const isLanguageSupported = (languageCode) => {
  const code = languageCode?.toUpperCase();
  return code in translations;
};

/**
 * Get list of all supported language codes
 * @returns {string[]} Array of supported language codes
 */
export const getSupportedLanguages = () => {
  return Object.keys(translations);
};

export default translations;
